import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment.development';
import {
    DocumentCaseData,
    DocumentCaseDataApiResponse,
    DocumentCaseDataRaw,
    DocumentId,
    UploadSignedDocumentResponse,
} from './document.models';
import { DOC_API, DOC_TYPE_MAP } from './document-api.constants';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    private readonly apiBase = environment.apiUrl;

    constructor(private http: HttpClient) { }

    // ── GET /api/case-data/{caseId}/1 ────────────────────────────────────────
    // Fetches cover-letter variables (document_type = 1).
    // X-Skip-Loader suppresses the full-page overlay — button spinner is used instead.
    getCoverLetterCaseData(caseId: number | string): Observable<DocumentCaseData> {
        return this.http
            .get<DocumentCaseDataApiResponse>(
                `${this.apiBase}${DOC_API.CASE_DATA_BY_TYPE(caseId, 1)}`,
                { headers: { 'X-Skip-Loader': '1' } }
            )
            .pipe(map(response => this.mapRawToCaseData(response.data)));
    }

    // ── GET /api/case-data/{caseId}/{documentType} ────────────────────────────
    // Fetches document-specific patient/provider/offer variables.
    // frontendDocIndex: 0=Acknowledgment, 1=ATF, 2=HIPAA, 3=Release of Lien
    // X-Skip-Loader suppresses the full-page overlay — button spinner is used instead.
    getCaseDataByType(
        caseId: number | string,
        frontendDocIndex: 0 | 1 | 2 | 3
    ): Observable<DocumentCaseData> {
        const apiDocType = DOC_TYPE_MAP[frontendDocIndex];
        return this.http
            .get<DocumentCaseDataApiResponse>(
                `${this.apiBase}${DOC_API.CASE_DATA_BY_TYPE(caseId, apiDocType)}`,
                { headers: { 'X-Skip-Loader': '1' } }
            )
            .pipe(map(response => this.mapRawToCaseData(response.data)));
    }

    private mapRawToCaseData(raw: DocumentCaseDataRaw): DocumentCaseData {
        const lienBalance = raw.current_lien_balance != null ? parseFloat(raw.current_lien_balance) : null;
        return {
            case_id: null,
            patient_first_name: raw.patient_first_name,
            patient_last_name: raw.patient_last_name,
            patient_dob: raw.client_dob ? raw.client_dob.substring(0, 10) : null,
            patient_address: raw.patient_address,
            provider_name: raw.provider_name,
            account_number: raw.account_number,
            date_of_service: raw.date_of_service,
            full_billed_charges: lienBalance,
            trust_amount: lienBalance,
            offer_amount: raw.offer_amount != null ? parseFloat(raw.offer_amount) : null,
            attorney_name: raw.attorney_name,
            attorney_firm: raw.firm_name,
            ssn_last4: raw.ssn_last4,
        };
    }

    // ── @deprecated — Use getCaseDataByType instead ───────────────────────────
    getCaseData(caseId: number | string): Observable<DocumentCaseData> {
        return this.http.get<DocumentCaseData>(
            `${this.apiBase}${DOC_API.CASE_DATA(caseId)}`
        );
    }

    // ── POST /api/documents/render-signed ────────────────────────────────────
    // Server-side PDF rendering via Gotenberg (headless Chromium).
    // Returns the rendered PDF as a Blob — no client-side html2canvas / pdf-lib.
    renderSignedDocument(
        caseId: number | string,
        documentType: number,
        signaturePngBase64: string,
        signedAt: string,
        signerName: string
    ): Observable<Blob> {
        return this.http.post(
            `${this.apiBase}${DOC_API.RENDER_SIGNED}`,
            {
                case_id:       Number(caseId),
                document_type: documentType,
                signature_png: signaturePngBase64,
                signed_at:     signedAt,
                signer_name:   signerName,
            },
            { responseType: 'blob' }
        );
    }

    // ── POST /api/documents/upload-signed ────────────────────────────────────
    uploadSignedDocument(
        caseId: number | string,
        documentId: DocumentId,
        pdfBlob: Blob,
        signedAt: string
    ): Observable<UploadSignedDocumentResponse> {
        const form = new FormData();
        form.append('case_id', caseId.toString());
        form.append('document_id', documentId.toString());
        form.append('signed_at', signedAt);
        form.append('pdf_file', pdfBlob, `document-${documentId}-signed.pdf`);

        return this.http.post<UploadSignedDocumentResponse>(
            `${this.apiBase}${DOC_API.UPLOAD_SIGNED}`,
            form
        );
    }

}

